import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export type ConfirmationPopupProviderState = {
  isPopupVisible: boolean;
  title: string;
  text: string;
  confirmText: string;
  cancelText: string;
  shouldPopUp: boolean;
  confirm: (confirm: boolean) => void;
  enablePopUpGuard: (enable?: boolean) => void;
  showConfirmationPopUp: (
    action: () => void,
    title: string,
    text: string,
    confirmText?: string | undefined,
    cancelText?: string | undefined,
  ) => void;
  showDefaultConfirmationPopUp: (props: ShowDefaultConfirmationPopUpProps) => void;
};

type ShowDefaultConfirmationPopUpProps = { action: () => void; shouldShow?: boolean };

const useConfirmationPopupProviderState = (): ConfirmationPopupProviderState => {
  const { t } = useTranslation('confirmationPopup');

  const [isPopupVisible, setIsPopupVisible] = useState<boolean>(false);
  const [shouldPopUp, setShouldPopUp] = useState(false);
  const [text, setText] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [confirmText, setConfirmText] = useState<string>('');
  const [cancelText, setCancelText] = useState<string>('');

  const [confirmationAction, setConfirmationAction] = useState<() => () => void>();

  const showDefaultConfirmationPopUp = ({ action, shouldShow = true }: ShowDefaultConfirmationPopUpProps): void => {
    if (shouldShow) {
      setConfirmationAction(() => action);
      setIsPopupVisible(true);
      setText(t('body'));
      setTitle(t('title'));
      setConfirmText(t('primaryButtonText'));
      setCancelText(t('secondaryButtonText'));
    } else {
      action();
    }
  };

  const showConfirmationPopUp = (action: () => void, title: string, text: string, confirmText?: string, cancelText?: string): void => {
    setConfirmationAction(() => action);
    setIsPopupVisible(true);
    setText(text);
    setTitle(title);
    setConfirmText(confirmText ?? t('primaryButtonText'));
    setCancelText(cancelText ?? t('secondaryButtonText'));
  };

  const confirm = (confirm: boolean): void => {
    setIsPopupVisible(false);

    if (confirm && confirmationAction) {
      setShouldPopUp(false);
      confirmationAction();
      setConfirmationAction(undefined);
    }
  };

  const enablePopUpGuard = (enable = true): void => {
    setShouldPopUp(enable);
  };

  return {
    isPopupVisible,
    title,
    text,
    confirmText,
    cancelText,
    shouldPopUp,
    confirm,
    enablePopUpGuard,
    showConfirmationPopUp,
    showDefaultConfirmationPopUp,
  };
};

export default useConfirmationPopupProviderState;
